/**
 * EmailLoginPage — landing for the web login link
 * (/api/auth/email-login/request). The token is in the fragment; it is only
 * exchanged for a session after the user clicks, so a mail scanner that opens
 * the link (even with JavaScript) does not spend it or sign in.
 */

import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { completeLogin } from "@/lib/passkey";
import { emailLoginToken } from "./emailLoginToken";

const PANEL_CLASS =
  "rounded-[32px] border border-[rgba(10,52,96,0.10)] bg-white/92 p-8 shadow-[0_24px_64px_rgba(10,52,96,0.10)] backdrop-blur-xl sm:p-10";
const PRIMARY_BUTTON_CLASS =
  "w-full rounded-full bg-[var(--cs-title-navy)] px-5 py-3.5 text-[15px] font-semibold text-white shadow-[0_16px_34px_rgba(10,52,96,0.18)] transition-colors duration-200 hover:bg-[var(--cs-title-navy)] disabled:cursor-not-allowed disabled:opacity-50";

export function EmailLoginPage() {
  const nav = useNavigate();
  const token = useMemo(() => emailLoginToken(window.location.hash), []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(
    token ? "" : "Deze link is onvolledig. Vraag een nieuwe inloglink aan op de inlogpagina.",
  );

  const handleLogin = useCallback(async () => {
    if (!token) return;
    setBusy(true);
    setError("");
    try {
      await completeLogin(token);
      // Drop the spent token from the address bar and history.
      window.history.replaceState({}, "", "/login/email");
      nav("/download", { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      setError(
        /expired|invalid|used/i.test(message)
          ? "Deze inloglink is verlopen of al gebruikt. Vraag een nieuwe aan op de inlogpagina."
          : message || "Inloggen mislukt.",
      );
      setBusy(false);
    }
  }, [nav, token]);

  return (
    <div className="cs-marketing min-h-screen bg-[linear-gradient(180deg,#fcfbf7_0%,#ffffff_22%,#f3f9fd_100%)] px-4 py-12 sm:px-6 sm:py-16">
      <div className="mx-auto w-full max-w-md space-y-6">
        <div className={PANEL_CLASS}>
          <div className="mb-6 text-center">
            <h1 className="text-[32px] font-semibold tracking-[-0.04em] text-[var(--cs-title-navy)]">
              Inloggen
            </h1>
          </div>

          {error ? (
            <div className="mb-5 rounded-[18px] border border-[#F3C7CD] bg-[#FFF4F6] p-4 text-sm text-[#B42318]">
              {error}{" "}
              <a href="/login" className="underline">
                Naar de inlogpagina
              </a>
            </div>
          ) : null}

          {token ? (
            <>
              <p className="mb-6 text-center text-[15px] text-[rgba(95,131,160,0.96)]">
                Klik om in te loggen in deze browser.
              </p>
              <button
                type="button"
                onClick={() => void handleLogin()}
                disabled={busy}
                className={PRIMARY_BUTTON_CLASS}
              >
                {busy ? "Bezig…" : "Inloggen"}
              </button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
